import { useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { WizardStepBaseProps } from '../../../../wizard/interfaces/wizard-step-base-props';
import { useWizardStepData } from '../../../../wizard/hooks/useWizardStepData';
import {
  RawDestination,
  RawDestinationType,
  Destination,
  DestinationType
} from '../../../../shared/interfaces/destination';
import { CreatePipelineSelectDestinationTypeWizardData } from './interfaces/create-pipeline-destination-type-step-wizard-data';
import { CreatePipelineDestinationTypeStepWizardDataFactory } from './factories/createPipelineDestinationTypeStepWizardDataFactory';
import { SelectDestinationType } from '../../../../destinations/SelectDestinationType/SelectDestinationType';
import { DestinationAPI } from '../../../../destinations/api/DestinationAPI';
import { DestinationList } from '../../../../destinations/DestinationList/DestinationList';
import { GenericApiResponse } from '../../../../shared/interfaces/generics';
import { DestinationListReducerState } from '../../../../destinations/DestinationList/cache/interface';
import { PipelineCreateNewDestinationRadioGroup } from './components/PipelineCreateNewDestinationRadioGroup';
import { PipelineCreateSelectDestinationError } from './components/PipelineCreateSelectDestinationError';
import { PipelineCreateSelectDestinationLoadingSkeleton } from './components/PipelineCreateSelectDestinationLoadingSkeleton';
import { getCreatePipelineSelectDestinationPageData } from './utils/createPipelineSelectDestinationUtil';
import styles from './CreatePipelineSelectDestinationType.module.scss';
import { useCreatePipelineContext } from '../../hooks/useCreatePipelineContext';
import { useAnalyticsTracker } from '../../../../tracking/hooks/useAnalyticsTracker';
import { CreatePipelineTrackers } from '../../../tracking/create-pipeline-trackers';
import { UpgradePipelineBanner } from '../../../UpgradePipeline/UpgradePipelineBanner';

export function CreatePipelineSelectDestinationType({
  completeStep,
  initialData,
  onDataChange,
  updateWizardStepFooterProps
}: WizardStepBaseProps) {
  const initialDataRef = useRef<CreatePipelineSelectDestinationTypeWizardData>(
    CreatePipelineDestinationTypeStepWizardDataFactory(initialData || {})
  );
  const [data, setData] = useWizardStepData<CreatePipelineSelectDestinationTypeWizardData>(
    initialDataRef.current,
    onDataChange
  );

  const { updateState, pipeline, standardPipelineInfo, isUpgradingPipeline } =
    useCreatePipelineContext();
  const { eventTrack } = useAnalyticsTracker();

  useEffect(() => {
    fetchPageData();

    updateWizardStepFooterProps({
      entity: 'Destination'
    });

    return () => {
      updateWizardStepFooterProps({});
    };
  }, []);

  const fetchPageData = () => {
    const fetchDestinationTypes =
      !initialDataRef.current.createNewDestinationPageData.destinationTypes ||
      initialDataRef.current.createNewDestinationPageData.destinationTypes.length === 0;
    const fetchExistingDestinationsList =
      !initialDataRef.current.existingDestinationPageData.destinations ||
      initialDataRef.current.existingDestinationPageData.destinations.length === 0;

    const promises: [
      Promise<GenericApiResponse<RawDestination[]>>?,
      Promise<GenericApiResponse<RawDestinationType[]>>?
    ] = [];

    if (fetchExistingDestinationsList) {
      promises.push(DestinationAPI.getDestinations());
    }

    if (fetchDestinationTypes) {
      promises.push(DestinationAPI.getDestinationTypes());
    }

    if (promises.length > 0) {
      setData(prevState => ({ ...prevState, loadingPageData: true }));

      Promise.all(promises)
        .then(([existingDestinationTypeResponse, destinationTypeResponse]) => {
          setData(prevState => ({
            ...prevState,
            ...getCreatePipelineSelectDestinationPageData(
              prevState,
              existingDestinationTypeResponse,
              destinationTypeResponse
            )
          }));
        })
        .catch(e => {
          setData(prevState => ({ ...prevState, loadingPageData: false, apiError: e }));
        });
    }
  };

  const onDestinationTypeSelect = (destinationType: DestinationType) => {
    eventTrack({
      action: CreatePipelineTrackers.PIPELINE_TRACKER_CREATE_PIPELINE_SELECT_DESTINATION_TYPE,
      properties: {
        destinationType: destinationType.destinationType,
        pipelineId: pipeline!.id
      }
    });

    updateState({
      destination: null,
      destinationType
    });

    completeStep();
  };

  const onDestinationSelect = (destination: Destination) => {
    eventTrack({
      action: CreatePipelineTrackers.PIPELINE_TRACKER_CREATE_PIPELINE_SELECT_EXISTING_DESTINATION,
      properties: {
        destinationType: destination.destinationType.destinationType,
        destinationId: destination.id,
        pipelineId: pipeline!.id
      }
    });

    updateState({
      destinationType: null,
      destination
    });

    completeStep();
  };

  const saveExistingDestinationPageData = (
    destinationListPageData: DestinationListReducerState
  ) => {
    setData(prevState => ({
      ...prevState,
      existingDestinationPageData: {
        ...prevState.existingDestinationPageData,
        ...destinationListPageData
      }
    }));
  };

  const saveNewDestinationPreference = (configureNewDestination: boolean) => {
    setData(prevState => ({
      ...prevState,
      configureNewDestination
    }));
  };

  const onChangeCreateNewDestinationPreference = (preference: string) => {
    const createNewDestination = preference === 'true';

    eventTrack({
      action:
        CreatePipelineTrackers.PIPELINE_TRACKER_CREATE_PIPELINE_CREATE_NEW_DESTINATION_RADIO_CLICK,
      properties: {
        createNewDestination,
        pipelineId: pipeline!.id
      }
    });

    saveNewDestinationPreference(createNewDestination);
  };

  const renderLoadingShimmer = data.loadingPageData;
  const renderPageError = !!data.apiError && !renderLoadingShimmer;
  const renderPageData =
    !renderLoadingShimmer &&
    !renderPageError &&
    ((data.createNewDestinationPageData.destinationTypes &&
      data.createNewDestinationPageData.destinationTypes.length > 0) ||
      (data.existingDestinationPageData.destinations &&
        data.existingDestinationPageData.destinations.length > 0));
  const renderCreateNewDestination = !renderLoadingShimmer && data.configureNewDestination;
  const renderSelectExistingDestination = !renderCreateNewDestination;

  return (
    <>
      {isUpgradingPipeline ? (
        <UpgradePipelineBanner
          standardPipelineInfo={standardPipelineInfo}
          isObjectConfigPage={false}
        />
      ) : null}

      <div className='scrollable-content'>
        <div className={clsx('m-auto center-flex-col', styles.selectDestinationTypeContainer)}>
          {renderLoadingShimmer ? <PipelineCreateSelectDestinationLoadingSkeleton /> : null}

          {renderPageError ? (
            <PipelineCreateSelectDestinationError
              error={data.apiError as object}
              retry={fetchPageData}
            />
          ) : null}

          {renderPageData ? (
            <>
              <PipelineCreateNewDestinationRadioGroup
                currentValue={data.configureNewDestination ? 'true' : 'false'}
                defaultValue={initialDataRef.current.configureNewDestination ? 'true' : 'false'}
                onChange={onChangeCreateNewDestinationPreference}
              />

              {renderCreateNewDestination ? (
                <SelectDestinationType
                  onDestinationTypeSelect={onDestinationTypeSelect}
                  initialData={data.createNewDestinationPageData}
                />
              ) : null}

              {renderSelectExistingDestination ? (
                <DestinationList
                  wizardMode
                  onDestinationSelect={onDestinationSelect}
                  onStateChange={saveExistingDestinationPageData}
                  newListComponentProps={{
                    primaryCTAProps: {
                      children: 'Configure New Destination',
                      onClick: () => onChangeCreateNewDestinationPreference('true')
                    }
                  }}
                />
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}
